import { isValid, subYears, isBefore, isToday, isSameDay } from "date-fns";
import {
  emailRegex,
  nameRegex,
  nzMobilePhoneRegex,
  nzPhoneRegex,
  passwordRegex,
  usernameRegex
} from "./regularExpressions";
import formatMoney from "./formatMoney";

export const isBankAccount = ({ field }) => {
  const bankAccount = bankValidator.validate(field.value.toString());
  return [bankAccount, field.$extra.errorMessages.invalid];
};

export const isRequired = ({ field }) => {
  const { value, disabled } = field;
  const required = value;
  if (!disabled) {
    return [required, field.$extra.errorMessages.empty];
  }
  return [disabled];
};

export const isEmail = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const sanitized = value && value.toString().trim();
    field.set(sanitized);
    const email = emailRegex.test(sanitized);
    return [email, field.$extra.errorMessages.invalid];
  }
  return [disabled];
};

export const isValidUsername = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const sanitized = value && value.toString().trim();
    field.set(sanitized);
    const username = usernameRegex.test(sanitized);
    return [username, field.$extra.errorMessages.invalid];
  }
  return [disabled];
};

export const isUsername = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const sanitized = value && value.toString().trim();
    field.set(sanitized);
    const username = sanitized;
    return [username, field.$extra.errorMessages.invalid];
  }
  return [disabled];
};

const sanitizeName = value => {
  return value
    .toString()
    .trim()
    .replace(
      /[^a-zA-ZÀ-ÖØ-öø-ÿ\u0101\u0113\u012b\u014d\u016b\u0100\u0112\u012a\u014c\u016a'.-\s]/g,
      ""
    ) // Remove not allowed characters
    .replace(/(\s?)(?=[-'.])/gi, "") // Converts "Contreras -Rodriguez" to "Contreras-Rodriguez"
    .replace(/([-'.]{1})(\s?)/gi, "$1") // Converts "Contreras- Rodriguez" to "Contreras-Rodriguez"
    .replace(/([-'.]){1,255}/gi, "$1") // Removes repeated characters e.g. "Contreras---Rodriguez" to "Contreras-Rodriguez"
    .replace(
      /^([^a-zA-ZÀ-ÖØ-öø-ÿ\u0101\u0113\u012b\u014d\u016b\u0100\u0112\u012a\u014c\u016a])/,
      ""
    ) // Removes not allowed characters from the beginning
    .replace(
      /([^a-zA-ZÀ-ÖØ-öø-ÿ\u0101\u0113\u012b\u014d\u016b\u0100\u0112\u012a\u014c\u016a]$)/,
      ""
    ); // Removes not allowed characters from the end
};

export const isName = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    if (value) {
      const sanitized = sanitizeName(value);
      field.set(sanitized);
      const name = sanitized === "" || nameRegex.test(sanitized);
      return [name, field.$extra.errorMessages.invalid];
    }
    return [true];
  }
  return [disabled];
};

export const isMobile = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const sanitized = value && value.toString().replace(/\D+/g, "");
    field.set(sanitized);
    const mobile = nzMobilePhoneRegex.test(sanitized);
    return [mobile, field.$extra.errorMessages.invalid];
  }
  return [disabled];
};

export const isPhone = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const sanitized = value && value.toString().replace(/\D+/g, "");
    field.set(sanitized);
    const phone = nzPhoneRegex.test(sanitized);
    return [phone, field.$extra.errorMessages.invalid];
  }
  return [disabled];
};

export const isPassword = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const password = passwordRegex.test(value.toString());
    return [password, field.$extra.errorMessages.invalid];
  }
  return [disabled];
};

export const isDate = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const toDate = value && new Date(value);
    const date = isValid(toDate);
    return [date, field.$extra.errorMessages.invalid];
  }
  return [disabled];
};

export const isEighteen = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const toDate = value && new Date(value);
    const eighteenYears = new Date(subYears(new Date(), 18));
    const eighteen =
      isBefore(toDate, eighteenYears) || isSameDay(toDate, eighteenYears);
    return [eighteen, field.$extra.errorMessages.tooYoung];
  }
  return [disabled];
};

export const isNotFuture = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    const toDate = value && new Date(value);
    const now = new Date();
    const notFuture = isBefore(toDate, now) || isToday(now);
    return [notFuture, field.$extra.errorMessages.noFutureDate];
  }
  return [disabled];
};

export const isAtLeast = ({ field }) => {
  const { value, disabled } = field;
  if (!disabled) {
    if (!field.$extra.minValue) {
      return [true];
    }
    let toNumber = value;
    if (typeof value === "string") {
      toNumber = value.replace(/[^\d.]/g, "");
      toNumber = parseFloat(toNumber);
    }
    const isEqualOrGreater = toNumber >= field.$extra.minValue;
    return [
      isEqualOrGreater,
      `${field.$extra.errorMessages.invalid} ${formatMoney(
        field.$extra.minValue
      )}`
    ];
  }
  return [disabled];
};

export const isNotMoreThan = ({ field }) => {
  const {
    value,
    disabled,
    $extra: { maxValue }
  } = field;
  if (!disabled) {
    if (!value) {
      field.set(0);
      return [true];
    }
    if (!maxValue) {
      return [true];
    }
    let toNumber = value;
    let max = parseFloat(maxValue);

    if (typeof toNumber === "string") {
      toNumber = toNumber.replace(/[^\d.]/g, "");
      toNumber = parseFloat(toNumber);
    }

    field.set(toNumber.toFixed(2));
    const isEqualOrLower = toNumber <= max;

    return [
      isEqualOrLower,
      `${field.$extra.errorMessages.invalid} ${formatMoney(
        field.$extra.maxValue
      )}`
    ];
  }
  return [disabled];
};
